/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { HEADER_PRIMARY_KEY } from '@/features/header/api/getHeader';
import { getAxiosForClient } from '@/lib/axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deletePreferredVehicleCookies } from '@/utils/analytics/dataLayer/preferredVehicleCookies';
import {
  getFullKey as getVehiclesFullKey,
  selector as getVehiclesSelector,
} from './getVehiclesACES';
import { dispatchEvent } from '@/utils/eventPublisher';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { sessionStorage } from '@/utils/sessionStorage';
import logger from '@/utils/logger';

const deleteDefaultVehicle = async (previousVehicleID: string) => {
  try {
    const response = await getAxiosForClient().delete(
      '/ecomm/b2c/v2/currentUser/vehicles/defaultVehicle',
    );
    sessionStorage.setItem('previousPreferredVehicleId', previousVehicleID);
    // we're not able to type delete() function from axios due to old version that we're using
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return getVehiclesSelector(response.data);
  } catch (error) {
    logger.error({
      meta: error,
      message: 'Error deleting selected vehicle',
    });
  }
};

const getKey = () => ['deleteDefaultVehicle'];

export const useDeleteDefaultVehicleAcesMutation = () => {
  const queryClient = useQueryClient();
  const previousPreferredVehicle = usePreferredVehicle();
  const catalogVehicleId = previousPreferredVehicle?.catalogVehicleId ?? '0';

  return useMutation({
    mutationFn: () => deleteDefaultVehicle(catalogVehicleId),
    mutationKey: getKey(),
    onSuccess: async (data) => {
      deletePreferredVehicleCookies();
      if (data) {
        // Update both query variants - with and without includeRepairGuides
        queryClient.setQueryData(getVehiclesFullKey({ includeRepairGuides: true }), data);
        queryClient.setQueryData(getVehiclesFullKey(), data);
      }
      await queryClient.refetchQueries({
        queryKey: [HEADER_PRIMARY_KEY],
      });
      dispatchEvent('preferredVehicleUpdated', { vehicleSet: false });
    },
  });
};
